import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Moment from 'moment';
import axiosInstance from '../../config/axiosApi/axiosApi';
import APIs from '../../Constants/apis';
import showErrorToast from '../../components/CustomComponents/toast/toast';
import ServeIQLoader from '../../components/CustomComponents/preLoad/serveiqLoader';
import { floorLoyaltyPoints } from '../../utils/loyaltyPoints';
import DashboardHeroStats from './DashboardHeroStats';
import DashboardWeeklySales from './DashboardWeeklySales';
import DashboardSalesCharges from './DashboardSalesCharges';
import DashboardBottomPanels from './DashboardBottomPanels';
import { resolveTenderChartBucket } from '../../utils/resolveTenderChartBucket';
import './dashboard-figma-tokens.css';

const currencySymbols = { GBP: '£', USD: '$', EUR: '€' };
const getCurrencySymbol = (code) => currencySymbols[code] || code;

function DashboardV1() {
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const { profileData } = useSelector((state) => state.auth);
  const { start: rangeStartIso, end: rangeEndIso } = useSelector(
    (state) => state.dashboardDateRange
  );
  const compCurrency = profileData?.compCurrency || 'GBP';

  const [loading, setLoading] = useState(false);
  const [todaySale, setTodaySale] = useState(null);
  const [dashboardSms, setDashboardSms] = useState({
    total: 0,
    used: 0,
    left: 0,
    packageName: '—',
    activationDate: '—'
  });
  const [dashboardReservations, setDashboardReservations] = useState([]);
  const [reservationBookingSpent, setReservationBookingSpent] = useState(0);

  const rangeStart = useMemo(
    () => Moment(rangeStartIso).startOf('day'),
    [rangeStartIso]
  );
  const rangeEnd = useMemo(
    () => Moment(rangeEndIso || rangeStartIso).startOf('day'),
    [rangeEndIso, rangeStartIso]
  );
  const saleReportParams = useMemo(
    () => ({
      CompID,
      StartDate: rangeStart.format('YYYY-MM-DD'),
      EndDate: rangeEnd.format('YYYY-MM-DD')
    }),
    [CompID, rangeStart, rangeEnd]
  );
  const [topProducts, setTopProducts] = useState([]);
  const [loyaltySummary, setLoyaltySummary] = useState(null);
  const [isLoyaltyEnabled, setIsLoyaltyEnabled] = useState(false);
  const expectedTenderBucket = useMemo(
    () => resolveTenderChartBucket(rangeStart, rangeEnd),
    [rangeStart, rangeEnd]
  );
  const [tenderChart, setTenderChart] = useState({
    categories: [],
    cash: [],
    card: [],
    online: [],
    bucket: expectedTenderBucket
  });

  // Immediate subtitle (By hour / day / week) when range changes; series refresh via fetch.
  useEffect(() => {
    setTenderChart((prev) =>
      prev.bucket === expectedTenderBucket
        ? prev
        : { ...prev, bucket: expectedTenderBucket }
    );
  }, [expectedTenderBucket]);

  const formatCurrency = (value) => {
    if (value == null || isNaN(value)) return `${getCurrencySymbol(compCurrency)} 0.00`;
    return `${getCurrencySymbol(compCurrency)} ${parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const stats = todaySale?.stats || todaySale?.Stats || null;
  const salesKpis = todaySale?.salesKpis || todaySale?.SalesKpis || null;
  const reportCatStats = todaySale?.reportCatStats || todaySale?.ReportCatStats || [];
  const menuStats = todaySale?.menuStats || todaySale?.MenuStats || null;
  const comboStats = todaySale?.comboStats || todaySale?.ComboStats || null;

  const fetchTodaySale = useCallback(async () => {
    if (!CompID) return;
    try {
      const res = await axiosInstance.get(APIs.GET_TODAY_SALE, {
        params: saleReportParams
      });
      if (res.data?.status ?? res.data?.Status) {
        setTodaySale(res.data?.data ?? res.data?.Data ?? null);
      }
    } catch (err) {
      showErrorToast('Failed to load sales for the selected period');
      console.error(err);
    }
  }, [CompID, saleReportParams]);

  const fetchTopProducts = useCallback(async () => {
    if (!CompID) return;
    try {
      const res = await axiosInstance.get(APIs.GET_DASHBOARD_TOP_5_PRODUCTS, {
        params: saleReportParams
      });
      if (res.data?.status ?? res.data?.Status) {
        const list = res.data?.data ?? res.data?.Data ?? [];
        setTopProducts(Array.isArray(list) ? list : []);
      } else {
        setTopProducts([]);
      }
    } catch (err) {
      setTopProducts([]);
      console.error(err);
    }
  }, [CompID, saleReportParams]);

  const fetchTenderSalesChart = useCallback(async () => {
    if (!CompID) return;
    // Keep auto so backend owns resolution; expectedTenderBucket is subtitle/fallback for 1-day → hour.
    const fallbackBucket = resolveTenderChartBucket(rangeStart, rangeEnd);
    try {
      const res = await axiosInstance.get(APIs.GET_TENDER_SALES_FOR_CHART, {
        params: {
          CompID,
          StartDate: rangeStart.format('YYYY-MM-DD'),
          EndDate: rangeEnd.format('YYYY-MM-DD'),
          Bucket: 'auto'
        }
      });
      const ok = res.data?.status ?? res.data?.Status;
      const data = res.data?.data ?? res.data?.Data;
      if (ok && data) {
        const toNums = (arr) =>
          (Array.isArray(arr) ? arr : []).map((v) => Number(v) || 0);
        const apiBucket = String(data.bucket ?? data.Bucket ?? '').toLowerCase();
        setTenderChart({
          categories: Array.isArray(data.categories ?? data.Categories)
            ? data.categories ?? data.Categories
            : [],
          cash: toNums(data.cash ?? data.Cash),
          card: toNums(data.card ?? data.Card),
          online: toNums(data.online ?? data.Online),
          bucket: ['hour', 'day', 'week'].includes(apiBucket)
            ? apiBucket
            : fallbackBucket
        });
      } else {
        setTenderChart({
          categories: [],
          cash: [],
          card: [],
          online: [],
          bucket: fallbackBucket
        });
      }
    } catch (err) {
      setTenderChart({
        categories: [],
        cash: [],
        card: [],
        online: [],
        bucket: fallbackBucket
      });
      console.error(err);
    }
  }, [CompID, rangeStart, rangeEnd]);

  const fetchLoyaltyRule = useCallback(async () => {
    if (!CompID) return;
    try {
      const res = await axiosInstance.get(APIs.GET_LOYALTY_RULE, {
        params: { compID: CompID }
      });
      const ok = res.data?.Status ?? res.data?.status;
      const rule = res.data?.Data ?? res.data?.data;
      // Loyalty is enabled if there's an active rule with Status = true
      if (ok && rule && rule.Status === true) {
        setIsLoyaltyEnabled(true);
      } else {
        setIsLoyaltyEnabled(false);
      }
    } catch (err) {
      setIsLoyaltyEnabled(false);
      console.error(err);
    }
  }, [CompID]);

  const fetchLoyaltySummary = useCallback(async () => {
    if (!CompID) return;
    try {
      const res = await axiosInstance.get(APIs.GET_LOYALTY_ANALYTICS, {
        params: {
          compID: CompID,
          startDate: rangeStart.format('YYYY-MM-DD'),
          endDate: rangeEnd.format('YYYY-MM-DD')
        }
      });
      const ok = res.data?.Status ?? res.data?.status;
      const data = res.data?.Data ?? res.data?.data;
      if (ok && data) setLoyaltySummary(data);
      else setLoyaltySummary(null);
    } catch (err) {
      setLoyaltySummary(null);
      console.error(err);
    }
  }, [CompID, rangeStart, rangeEnd]);

  const fetchDashboardSmsSummary = useCallback(async () => {
    if (!CompID) return;
    try {
      const res = await axiosInstance.get(APIs.BO_GET_DASHBOARD_SMS_SUMMARY);
      const ok = res.data?.status ?? res.data?.Status;
      const data = res.data?.data ?? res.data?.Data;
      if (ok && data) {
        const activationRaw = data.activationDate ?? data.ActivationDate;
        setDashboardSms({
          total: Number(data.smsTotal ?? data.SmsTotal ?? 0) || 0,
          used: Number(data.smsUsed ?? data.SmsUsed ?? 0) || 0,
          left: Number(data.smsLeft ?? data.SmsLeft ?? 0) || 0,
          packageName:
            String(data.activePackageName ?? data.ActivePackageName ?? '').trim() ||
            'SMS Credits',
          activationDate: activationRaw
            ? Moment(activationRaw).format('MMM D, YYYY')
            : '—'
        });
      } else {
        setDashboardSms({
          total: 0,
          used: 0,
          left: 0,
          packageName: '—',
          activationDate: '—'
        });
      }
    } catch (err) {
      setDashboardSms({
        total: 0,
        used: 0,
        left: 0,
        packageName: '—',
        activationDate: '—'
      });
      console.error(err);
    }
  }, [CompID]);

  const fetchDashboardUpcomingReservations = useCallback(async () => {
    if (!CompID) return;
    try {
      const res = await axiosInstance.get(APIs.BO_GET_DASHBOARD_UPCOMING_RESERVATIONS, {
        params: { CompID }
      });
      const ok = res.data?.status ?? res.data?.Status;
      const list = res.data?.data ?? res.data?.Data ?? [];
      if (ok && Array.isArray(list)) {
        setDashboardReservations(
          list.map((row, idx) => {
            const start = row.startDateTime ?? row.StartDateTime;
            return {
              id: row.id ?? row.ID ?? idx,
              name: row.customerName ?? row.CustomerName ?? '—',
              when: start ? Moment(start).format('MMM D - HH:mm') : '—',
              guests: Number(row.guestCount ?? row.GuestCount ?? 0) || 0,
              table: row.tableLabel ?? row.TableLabel ?? '—'
            };
          })
        );
      } else {
        setDashboardReservations([]);
      }
    } catch (err) {
      setDashboardReservations([]);
      console.error(err);
    }
  }, [CompID]);

  const fetchReservationBookingSpent = useCallback(async () => {
    if (!CompID) return;
    try {
      const res = await axiosInstance.get(APIs.GET_RESERVATION_STATS_SUMMARY, {
        params: {
          CompID,
          StartDate: rangeStart.format('YYYY-MM-DD'),
          EndDate: rangeEnd.format('YYYY-MM-DD')
        }
      });
      const ok = res.data?.status ?? res.data?.Status;
      const data = res.data?.data ?? res.data?.Data;
      if (ok && data) {
        setReservationBookingSpent(
          Number(data.bookingSpent ?? data.BookingSpent ?? 0) || 0
        );
      } else {
        setReservationBookingSpent(0);
      }
    } catch (err) {
      setReservationBookingSpent(0);
      console.error(err);
    }
  }, [CompID, rangeStart, rangeEnd]);

  const loadAll = useCallback(() => {
    if (!CompID) return;
    setLoading(true);
    Promise.all([
      fetchTodaySale(),
      fetchTopProducts(),
      fetchTenderSalesChart(),
      fetchLoyaltyRule(),
      fetchLoyaltySummary(),
      fetchDashboardSmsSummary(),
      fetchDashboardUpcomingReservations(),
      fetchReservationBookingSpent()
    ]).finally(() => setLoading(false));
  }, [
    CompID,
    fetchTodaySale,
    fetchTopProducts,
    fetchTenderSalesChart,
    fetchLoyaltyRule,
    fetchLoyaltySummary,
    fetchDashboardSmsSummary,
    fetchDashboardUpcomingReservations,
    fetchReservationBookingSpent
  ]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Hero + order counts from SalesKpis (GetTodaySale / Z preview for selected range)
  const grossSales = Number(salesKpis?.grossSales ?? salesKpis?.GrossSales ?? 0);
  const netVat = Number(salesKpis?.netVat ?? salesKpis?.NetVat ?? 0);
  // Order Count card: buckets must be mutually exclusive and sum to Total.
  // Legacy SP counted Running via kitchen status ("Open") while Completed used payment=sale,
  // so paid open tickets were double-counted. Prefer API values; if they still overlap, derive Running.
  const orderCompleted =
    Number(salesKpis?.completedOrders ?? salesKpis?.CompletedOrders ?? 0) || 0;
  const orderCancelled =
    Number(salesKpis?.cancelledOrders ?? salesKpis?.CancelledOrders ?? 0) || 0;
  const orderRefunds =
    Number(salesKpis?.refundedOrders ?? salesKpis?.RefundedOrders ?? 0) || 0;
  const orderTotal =
    Number(salesKpis?.totalOrders ?? salesKpis?.TotalOrders ?? 0) || 0;
  let orderRunning =
    Number(salesKpis?.runningOrders ?? salesKpis?.RunningOrders ?? 0) || 0;
  const orderPartsSum =
    orderCompleted + orderCancelled + orderRefunds + orderRunning;
  if (orderTotal > 0 && orderPartsSum > orderTotal) {
    orderRunning = Math.max(
      0,
      orderTotal - orderCompleted - orderCancelled - orderRefunds
    );
  }
  const orderCountStats = {
    total: orderTotal,
    completed: orderCompleted,
    cancelled: orderCancelled,
    refunds: orderRefunds,
    running: orderRunning
  };

  // Sales card: Other (remaining depts) | Wet | Tobacco | Combo — from reportCatStats / comboStats
  const catName = (c) => String(c.categoryTypeName ?? c.CategoryTypeName ?? '').trim().toLowerCase();
  const catSales = (c) => Number(c.totalSales ?? c.TotalSales ?? 0) || 0;
  const isWetCat = (c) => catName(c) === 'wet';
  const isTobaccoCat = (c) => {
    const n = catName(c);
    return n === 'tobacco' || n === 'smoke';
  };
  let wetSales = 0;
  let tobaccoSales = 0;
  let otherDeptSales = 0;
  if (Array.isArray(reportCatStats)) {
    reportCatStats.forEach((c) => {
      const amount = catSales(c);
      if (isWetCat(c)) wetSales += amount;
      else if (isTobaccoCat(c)) tobaccoSales += amount;
      else otherDeptSales += amount;
    });
  }
  if (menuStats) {
    otherDeptSales += Number(menuStats.totalSales ?? menuStats.TotalSales ?? 0) || 0;
  }
  const comboSalesValue = Number(comboStats?.totalSales ?? comboStats?.TotalSales ?? 0) || 0;
  const salesBreakdownItems = [
    { key: 'other', label: 'Food Sales', value: otherDeptSales },
    { key: 'wet', label: 'Wet Sales', value: wetSales },
    { key: 'tobacco', label: 'Tobacco Sales', value: tobaccoSales },
    { key: 'combo', label: 'Combo Sales', value: comboSalesValue }
  ];

  const inOutSales = {
    inSales: [
      {
        key: 'dineIn',
        label: 'Dine-ins',
        value: Number(stats?.dS_DineInSales ?? stats?.DS_DineInSales ?? 0) || 0
      },
      {
        key: 'reservations',
        label: 'Reservations',
        value: reservationBookingSpent
      }
    ],
    outSales: [
      {
        key: 'collection',
        label: 'Collection',
        value: Number(stats?.dS_TakeAwaySales ?? stats?.DS_TakeAwaySales ?? 0) || 0
      },
      {
        key: 'delivery',
        label: 'Delivery',
        value: Number(stats?.dS_DeliveriesSales ?? stats?.DS_DeliveriesSales ?? 0) || 0
      }
    ]
  };

  const loyaltyCustomers = loyaltySummary?.totalLoyaltyCustomers ?? loyaltySummary?.TotalLoyaltyCustomers ?? 0;
  const pointsInCirculation = loyaltySummary?.totalPointsInCirculation ?? loyaltySummary?.TotalPointsInCirculation ?? 0;
  const totalCustomers = loyaltySummary?.totalCustomers ?? loyaltySummary?.TotalCustomers ?? 0;
  const avgPointsBalance = loyaltySummary?.avgPointsBalance ?? loyaltySummary?.AvgPointsBalance ?? 0;
  const totalPointsEarned = loyaltySummary?.totalPointsEarned ?? loyaltySummary?.TotalPointsEarned ?? 0;
  const totalPointsSpent = loyaltySummary?.totalPointsSpent ?? loyaltySummary?.TotalPointsSpent ?? 0;
  const pointsEarnedInPeriod = loyaltySummary?.pointsEarnedInPeriod ?? loyaltySummary?.PointsEarnedInPeriod ?? 0;
  const pointsSpentInPeriod = loyaltySummary?.pointsSpentInPeriod ?? loyaltySummary?.PointsSpentInPeriod ?? 0;
  const onlineOrderCount = Number(stats?.dS_OnlineOrderCount ?? stats?.DS_OnlineOrderCount ?? 0) || 0;

  const heroStatCards = [
    {
      id: 'gross-sale',
      label: 'Total Sales',
      value: formatCurrency(grossSales),
      accent: 'mint'
    },
    {
      id: 'vat',
      label: 'VAT',
      value: formatCurrency(netVat),
      accent: 'silver'
    },
    {
      id: 'loyalty',
      label: 'Loyalty points given',
      value: floorLoyaltyPoints(pointsEarnedInPeriod).toLocaleString(),
      accent: 'gold'
    },
    {
      id: 'online-orders',
      label: 'Online Orders',
      value: onlineOrderCount.toLocaleString(),
      accent: 'silver'
    }
  ];

  // Only show loyalty panel if loyalty is enabled for the company
  const showLoyalty = isLoyaltyEnabled;

  return (
    <div className="dashboard-v1">
      {/* Figma hero: greeting + Total Sales / VAT from API + loyalty / online placeholders */}
      <DashboardHeroStats cards={heroStatCards} />

      {/* Figma: Sales chart (tender by hour/day/week) + Order Count */}
      <DashboardWeeklySales
        weekly={{
          ...tenderChart,
          grossSale: grossSales
        }}
        orders={orderCountStats}
        currencySymbol={getCurrencySymbol(compCurrency)}
      />

      {/* Figma: Sales / Discounts / Charges — depts + discount/voucher from GetTodaySale */}
      <DashboardSalesCharges
        sales={salesBreakdownItems}
        adjustments={[
          { key: 'discounts', label: 'Discounts', value: Number(stats?.dS_Discount ?? stats?.DS_Discount ?? 0) || 0 },
          { key: 'vouchers', label: 'Vouchers', value: Number(stats?.dS_VoucherDiscount ?? stats?.DS_VoucherDiscount ?? 0) || 0 }
        ]}
        charges={[
          { key: 'delivery', label: 'Delivery Charges', value: Number(stats?.dS_DeliveryCharges ?? stats?.DS_DeliveryCharges ?? 0) || 0 },
          { key: 'service', label: 'Service Charges', value: Number(stats?.dS_ServiceCharges ?? stats?.DS_ServiceCharges ?? 0) || 0 },
          { key: 'tips', label: 'Tips', value: Number(stats?.dS_Tip ?? stats?.DS_Tip ?? 0) || 0 },
          { key: 'extras', label: 'Extras', value: Number(stats?.dS_ExtraCharges ?? stats?.DS_ExtraCharges ?? 0) || 0 }
        ]}
        currencySymbol={getCurrencySymbol(compCurrency)}
      />

      {/* Figma: Top sellers | In/Out sales | SMS Details | Reservations */}
      <DashboardBottomPanels
        topProducts={topProducts}
        sales={inOutSales}
        sms={dashboardSms}
        reservations={dashboardReservations}
        currencySymbol={getCurrencySymbol(compCurrency)}
      />

      {loading ? (
        <div className="dashboard-v1-page-loader" aria-busy="true" role="status" aria-live="polite">
          <ServeIQLoader size={80} />
        </div>
      ) : (
      <>
      {showLoyalty && (
        <div className="row g-3 mb-3">
          <div className="col-12">
            <div className="dashboard-v1-sidebar-card dashboard-v1-card-compact">
              <div className="dashboard-v1-section-header dashboard-v1-section-header-with-action">
                <h3>Loyalty</h3>
                <Link to="/report/loyaltyReport" className="btn btn-sm btn-outline-primary">Full report</Link>
              </div>
              <div className="dashboard-v1-section-body">
                <div className="dashboard-v1-loyalty-grid">
                  <div className="dashboard-v1-loyalty-row">
                    <span className="dashboard-v1-loyalty-label">Loyalty customers</span>
                    <strong>{loyaltyCustomers}</strong>
                  </div>
                  <div className="dashboard-v1-loyalty-row">
                    <span className="dashboard-v1-loyalty-label">Total customers</span>
                    <strong>{totalCustomers}</strong>
                  </div>
                  <div className="dashboard-v1-loyalty-row">
                    <span className="dashboard-v1-loyalty-label">Points in circulation</span>
                    <strong>{floorLoyaltyPoints(pointsInCirculation).toLocaleString()}</strong>
                  </div>
                  <div className="dashboard-v1-loyalty-row">
                    <span className="dashboard-v1-loyalty-label">Avg points balance</span>
                    <strong>{floorLoyaltyPoints(avgPointsBalance).toLocaleString()}</strong>
                  </div>
                  <div className="dashboard-v1-loyalty-row">
                    <span className="dashboard-v1-loyalty-label">Points earned (period)</span>
                    <strong>{floorLoyaltyPoints(pointsEarnedInPeriod).toLocaleString()}</strong>
                  </div>
                  <div className="dashboard-v1-loyalty-row">
                    <span className="dashboard-v1-loyalty-label">Points spent (period)</span>
                    <strong>{floorLoyaltyPoints(pointsSpentInPeriod).toLocaleString()}</strong>
                  </div>
                  {(totalPointsEarned > 0 || totalPointsSpent > 0) && (
                    <>
                      <div className="dashboard-v1-loyalty-row">
                        <span className="dashboard-v1-loyalty-label">Total points earned</span>
                        <strong>{floorLoyaltyPoints(totalPointsEarned).toLocaleString()}</strong>
                      </div>
                      <div className="dashboard-v1-loyalty-row">
                        <span className="dashboard-v1-loyalty-label">Total points spent</span>
                        <strong>{floorLoyaltyPoints(totalPointsSpent).toLocaleString()}</strong>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}

export default DashboardV1;
